import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/shared/lib/cn";

export type TableIdLinkTone = "primary" | "neutral";
export type TableIdLinkSize = "sm" | "md";

export type TableIdLinkProps = {
  /** The ID or code string / ReactNode (e.g. "PR-20261001-B230BC", "MO-20261001-EC4666") */
  children?: ReactNode;
  /** Alternative prop for code text */
  value?: ReactNode;
  /** Target internal path for React Router navigation */
  to?: string;
  /** External href */
  href?: string;
  /** Click callback */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Visual tone: 'primary' (signature teal/green ID badge) or 'neutral' (subtle muted ID badge) */
  tone?: TableIdLinkTone;
  /** Size variant: 'md' (default in data tables) or 'sm' (compact) */
  size?: TableIdLinkSize;
  /** Monospace typography (default: true) */
  mono?: boolean;
  /** Title / tooltip text */
  title?: string;
  /** Additional custom classes */
  className?: string;
};

/**
 * Standard table ID / code chip link component.
 * Renders document / entity codes (mã mẻ, số MO, số DM, số PR, số SO...)
 * with standard rounded border chip styling, hover states and click isolation.
 */
export function TableIdLink({
  children,
  value,
  to,
  href,
  onClick,
  tone = "primary",
  size = "md",
  mono = true,
  title,
  className,
}: TableIdLinkProps) {
  const content = children ?? value;
  const isInteractive = Boolean(to || href || onClick);

  if (!content && content !== 0) {
    return <span className="text-text-muted">—</span>;
  }

  const baseClasses = cn(
    "inline-flex items-center shrink-0 whitespace-nowrap font-bold tabular-nums no-underline transition-colors duration-150 leading-tight",
    mono && "font-mono",
    size === "sm" ? "text-[11px]" : "text-[12px]",
    tone === "primary"
      ? [
          "text-primary",
          isInteractive && "hover:text-primary-hover hover:underline",
        ]
      : [
          "text-text-secondary",
          isInteractive && "hover:text-text hover:underline",
        ],
    isInteractive
      ? "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-focus-ring)] rounded-xs"
      : "cursor-default",
    className,
  );

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    // Android WebView often leaves a non-empty caret after a tap. Only block
    // navigation when the user actually dragged a text Range to copy.
    const selection = window.getSelection();
    const draggedCopy =
      selection != null &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      selection.type === "Range" &&
      selection.toString().trim().length > 0;
    if (draggedCopy) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // In tables, prevent triggering parent row selection/click
    e.stopPropagation();
    onClick?.(e);
  };

  if (to) {
    return (
      <Link
        to={to}
        title={title}
        className={baseClasses}
        onClick={handleClick}
      >
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        title={title}
        className={baseClasses}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        title={title}
        className={baseClasses}
        onClick={handleClick}
      >
        {content}
      </button>
    );
  }

  return (
    <span title={title} className={baseClasses}>
      {content}
    </span>
  );
}

export { TableIdLink as TableIdBadge };
